import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import { signupLegalMetadata } from '@/constants/legal';
import { setSentryUser } from '@/lib/sentry';

export type AppRole =
  | 'player'
  | 'coach'
  | 'referee'
  | 'club_staff'
  | 'association_staff'
  | 'tournament_organizer'
  | 'super_admin';

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  organization_id: string | null;
  created_at: string;
}

export interface Profile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  date_of_birth: string | null;
  gender: string | null;
  preferred_position?: string | null;
  preferred_hand: string | null;
  skill_level: string | null;
  skill_level_precise?: number | null;
  profile_completed?: boolean | null;

  // FFT
  fft_license_number: string | null;
  fft_player_id?: string | null;
  fft_ranking_points: number;
  fft_ranking_position: number | null;
  fft_best_ranking?: string | null;
  fft_evolution_points?: string | null;
  fft_tournaments_played?: number | null;
  fft_last_sync?: string | null;

  bio: string | null;

  // JSONB — tableau d'ids de tutoriels complétés (voir TutorialContext)
  tutorials_completed?: Json | null;

  // Date d'acceptation des CGU joueur (NULL = jamais acceptées, ex. compte Google)
  accepted_terms_at?: string | null;
}

interface SubscriptionInfo {
  subscribed: boolean;
  product_id?: string | null;
  subscription_end?: string | null;
  role?: string | null;
  roles?: string[]; // All active subscription roles
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  loading: boolean;
  /**
   * Connecté, mais aucun chargement de rôles n'a encore abouti. `roles` vaut
   * alors `[]` sans que cela veuille dire « aucun rôle » : `ProtectedRoute` doit
   * attendre, pas renvoyer vers l'onboarding.
   */
  rolesLoading: boolean;
  subscription: SubscriptionInfo | null;
  subscriptionLoading: boolean;
  activeRole: AppRole | null;
  setActiveRole: (role: AppRole) => void;
  refreshSubscription: (userId?: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** Recharge les rôles (dont `activeRole` s'il n'est pas encore défini) après une écriture dans `user_roles`. */
  refreshRoles: () => Promise<void>;
  /** `acceptedTerms` : passer `true` uniquement si l'utilisateur vient de cocher la case d'acceptation des CGU. */
  signUp: (email: string, password: string, firstName: string, lastName: string, redirectTo?: string, acceptedTerms?: boolean) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  hasRole: (role: AppRole) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isClubStaff: (organizationId: string) => Promise<boolean>;
  isClubAdmin: (organizationId: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRoleState] = useState<AppRole | null>(() => {
    // Load from localStorage on init
    const stored = localStorage.getItem('pala_active_role');
    return stored as AppRole | null;
  });
  const { toast } = useToast();

  const setActiveRole = (role: AppRole) => {
    setActiveRoleState(role);
    localStorage.setItem('pala_active_role', role);
  };

  const refreshSubscription = async (userId?: string) => {
    setSubscriptionLoading(true);
    
    try {
      // Utiliser le paramètre si fourni, sinon fallback sur user.id
      const currentUserId = userId || user?.id;
      
      if (!currentUserId) {
        console.error('❌ No user ID available for subscription check');
        setSubscription({ subscribed: false });
        setSubscriptionLoading(false);
        return;
      }
      
      console.log('🔍 Checking subscription for userId:', currentUserId);
      
      // Récupérer l'email de l'utilisateur
      const { data: userData } = await supabase.auth.getUser();
      const userEmail = userData.user?.email;
      
      // Bypass pour les comptes de test @test.com et @demo.pala.com
      if (userEmail?.endsWith('@test.com') || userEmail?.endsWith('@demo.pala.com')) {
        console.log('🧪 Test/Demo account detected, reading subscription from local DB');
        
        const { data: localSubs, error: localError } = await supabase
          .from('subscriptions')
          .select('*, plan:plan_id(code)')
          .eq('user_id', currentUserId)
          .eq('status', 'active');

        if (localError) {
          console.error('Error reading local subscription:', localError);
          setSubscription({ subscribed: false });
          setSubscriptionLoading(false);
          return;
        }

        if (localSubs && localSubs.length > 0) {
          // Collect all subscription roles
          const subscribedRoles = localSubs.map(s => s.role).filter(Boolean) as string[];

          setSubscription({
            subscribed: true,
            role: subscribedRoles[0], // Keep backward compatibility
            roles: subscribedRoles, // All roles
            subscription_end: localSubs[0].current_period_end,
            product_id: (localSubs[0] as any).plan?.code || localSubs[0].stripe_price_id
          });
          console.log('✅ Test subscriptions loaded:', {
            roles: subscribedRoles,
            count: localSubs.length
          });
        } else {
          setSubscription({ subscribed: false });
          console.log('⚠️ No active subscription found in local DB');
        }
      } else {
        // Pour les comptes réels, appeler Stripe
        const { data, error } = await supabase.functions.invoke('check-subscription');

        if (error) {
          console.error('Error checking subscription:', error);
          // Ne pas bloquer l'application, juste définir comme non-abonné
          setSubscription({ subscribed: false });
          setSubscriptionLoading(false);
          return;
        }

        setSubscription(data as SubscriptionInfo);
      }
    } catch (error) {
      console.error('Error refreshing subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Chaque chargement porte un numéro : seule la réponse du DERNIER lancé est
  // appliquée. Supabase émet SIGNED_IN à chaque retour sur l'onglet, et
  // fetchUserData enchaîne cinq requêtes : sans ce garde, un chargement parti
  // avant la validation du profil et revenu après remettait l'état d'avant.
  const rolesRequestRef = useRef(0);
  const profileRequestRef = useRef(0);

  const fetchRoles = async (userId: string): Promise<UserRole[]> => {
    // 1. Load user_roles (player, coach, referee, etc.)
    const { data: rolesData } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId);

    const allRoles: UserRole[] = rolesData || [];

    // 2. Load player memberships to organizations
    const { data: membershipData } = await supabase
      .from('organization_members') // Simpler query to get all memberships
      .select('*')
      .eq('user_id', userId);

    // 2. Load club_staff and create synthetic roles
    const { data: staffData } = await supabase
      .from('club_staff')
      .select('*, organization:organizations(type)')
      .eq('user_id', userId);

    if (membershipData) {
      membershipData.forEach((member: any) => {
        const syntheticRole: UserRole = {
          id: `synthetic-member-${member.id}`,
          user_id: userId,
          role: 'player', // The role is player, but linked to an org
          organization_id: member.organization_id,
          created_at: member.created_at,
        };
        allRoles.push(syntheticRole);
      });
    }

    if (staffData) {
      staffData.forEach((staff: any) => {
        const syntheticRole: UserRole = {
          id: `synthetic-${staff.id}`,
          user_id: userId,
          role: staff.organization.type === 'club' ? 'club_staff' : 'association_staff',
          organization_id: staff.organization_id,
          created_at: staff.created_at
        };
        allRoles.push(syntheticRole);
      });
    }

    // 3. Load board_permissions and create synthetic tournament_organizer role
    const { data: boardData } = await supabase
      .from('board_permissions')
      .select('id, organization_id, permissions, created_at')
      .eq('user_id', userId);

    if (boardData) {
      boardData.forEach((bp: any) => {
        const perms = bp.permissions as Record<string, boolean> || {};
        if (perms.tournaments && !allRoles.some(r => r.role === 'tournament_organizer')) {
          allRoles.push({
            id: `synthetic-board-tournament-${bp.id}`,
            user_id: userId,
            role: 'tournament_organizer',
            organization_id: bp.organization_id,
            created_at: bp.created_at,
          });
        }
      });
    }

    return allRoles;
  };

  /** Rend `null` si un chargement plus récent a été lancé entre-temps. */
  const loadRoles = async (userId: string): Promise<UserRole[] | null> => {
    const requestId = ++rolesRequestRef.current;
    const allRoles = await fetchRoles(userId);
    if (requestId !== rolesRequestRef.current) return null;
    setRoles(allRoles);
    setRolesLoaded(true);
    return allRoles;
  };

  const loadProfile = async () => {
    const requestId = ++profileRequestRef.current;
    // RPC plutôt que .select('*') : les colonnes de contact sont masquées par
    // GRANT, et `select=*` échouerait en 42501 (voir get_own_profile()).
    const { data: profileData } = await supabase
      .rpc('get_own_profile')
      .maybeSingle();

    if (profileData && requestId === profileRequestRef.current) {
      setProfile(profileData as Profile);
    }
  };

  // Initialize or re-validate activeRole
  // If the stored role doesn't exist in this user's roles, recalculate
  const ensureActiveRole = async (allRoles: UserRole[], userId: string) => {
    const storedRoleValid = activeRole && allRoles.some(r => r.role === activeRole);
    if ((!activeRole || !storedRoleValid) && allRoles.length > 0) {
      const defaultRole = await determineDefaultRole(allRoles, userId);
      setActiveRole(defaultRole);
    }
  };

  const fetchUserData = async (userId: string) => {
    // Immediately mark subscription as loading to prevent ProtectedRoute
    // from redirecting to onboarding before subscription data is fetched
    setSubscriptionLoading(true);

    try {
      const allRoles = await loadRoles(userId);

      await loadProfile();

      // Also fetch subscription status with explicit userId
      await refreshSubscription(userId);

      if (allRoles) await ensureActiveRole(allRoles, userId);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const refreshProfile = async () => {
    if (!user?.id) return;
    await loadProfile();
  };

  // À appeler après avoir écrit dans `user_roles` : les rôles en mémoire ne se
  // rechargent sinon qu'au retour sur l'onglet ou toutes les 60 s, et
  // ProtectedRoute renvoie vers l'onboarding tant qu'ils restent vides.
  const refreshRoles = async () => {
    if (!user?.id) return;
    const allRoles = await loadRoles(user.id);
    if (allRoles) await ensureActiveRole(allRoles, user.id);
  };

  const determineDefaultRole = async (userRoles: UserRole[], userId: string): Promise<AppRole> => {
    // Priority: super_admin > club_staff > association_staff > coach > player
    if (userRoles.some(r => r.role === 'super_admin')) return 'super_admin';
    
    // Check club/association staff via club_staff table
    const { data: staffRoles } = await supabase
      .from('club_staff')
      .select('*, organization:organizations(id, name, type)')
      .eq('user_id', userId);
    
    if (staffRoles && staffRoles.length > 0) {
      const hasClubStaff = staffRoles.some((r: any) => r.organization?.type === 'club');
      if (hasClubStaff) return 'club_staff';
      
      const hasAssocStaff = staffRoles.some((r: any) => r.organization?.type === 'association');
      if (hasAssocStaff) return 'association_staff';
    }
    
    if (userRoles.some(r => r.role === 'tournament_organizer')) return 'tournament_organizer';
    if (userRoles.some(r => r.role === 'coach')) return 'coach';

    // Use first available role instead of hardcoding 'player'
    if (userRoles.length > 0) return userRoles[0].role;
    return 'player'; // Absolute last resort (no roles found)
  };

  // Security enhancement: Periodic role refresh to prevent client-side manipulation
  // Refreshes every 60 seconds and on window focus to ensure roles match server state
  useEffect(() => {
    if (!user?.id) return;

    const refreshRolesPeriodically = async () => {
      try {
        // Re-fetch roles from server to ensure client state matches reality
        await loadRoles(user.id);
      } catch (error) {
        console.error('Error refreshing roles:', error);
      }
    };

    // Start periodic refresh after initial load (handled by fetchUserData)
    const intervalId = setInterval(refreshRolesPeriodically, 60000); // Every 60 seconds

    // Refresh on window focus for immediate validation when user returns
    const handleFocus = () => refreshRolesPeriodically();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setSentryUser(session?.user?.id ?? null);

        if (session?.user) {
          setTimeout(() => {
            fetchUserData(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setRolesLoaded(false);
          setSubscription(null);
          setSubscriptionLoading(false);
          // Clear stored role on logout so it doesn't leak to the next user
          setActiveRoleState(null);
          localStorage.removeItem('pala_active_role');
        }

        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setSentryUser(session?.user?.id ?? null);
      if (session?.user) {
        fetchUserData(session.user.id);
      } else {
        setSubscriptionLoading(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName: string, lastName: string, redirectTo?: string, acceptedTerms = false) => {
    const redirectUrl = redirectTo || `${window.location.origin}/onboarding/complete-profile`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
          // Preuve d'acceptation : `handle_new_user` recopie ces versions dans
          // `legal_acceptances` à la création du compte (20261014140000).
          ...(acceptedTerms ? signupLegalMetadata() : {}),
        }
      }
    });

    if (error) {
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
    } else if (data.user) {
      // Send welcome email after successful signup
      try {
        await supabase.functions.invoke('send-welcome-email', {
          body: {
            email: email,
            firstName: firstName,
          }
        });
        console.log('📧 Welcome email sent to', email);
      } catch (emailError) {
        console.error('Error sending welcome email:', emailError);
        // Don't block signup if email fails
      }
    }
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive",
      });
    }

    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      }
    });

    if (error) {
      toast({
        title: "Google sign in failed",
        description: error.message,
        variant: "destructive",
      });
    }

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    toast({
      title: "Signed out successfully",
    });
  };

  const resetPassword = async (email: string) => {
    // Supabase will trigger the custom-auth-email hook which sends
    // the branded email via Resend with the real recovery link
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }

    toast({
      title: "Email envoyé",
      description: "Vérifiez votre boîte mail pour réinitialiser votre mot de passe",
    });

    return { error };
  };

  const hasRole = (role: AppRole) => {
    return roles.some(r => r.role === role);
  };

  const isAdmin = () => {
    return hasRole('super_admin');
  };

  const isSuperAdmin = () => {
    return hasRole('super_admin');
  };

  const isClubStaff = async (organizationId: string) => {
    if (!user) return false;
    
    const { data } = await supabase
      .from('club_staff')
      .select('id')
      .eq('user_id', user.id)
      .eq('organization_id', organizationId)
      .single();
    
    return !!data;
  };

  const isClubAdmin = async (organizationId: string) => {
    if (!user) return false;
    
    const { data } = await supabase
      .from('club_staff')
      .select('role')
      .eq('user_id', user.id)
      .eq('organization_id', organizationId)
      .eq('role', 'admin')
      .single();
    
    return !!data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      roles,
      loading,
      // Au démarrage, getSession() ET l'événement INITIAL_SESSION lancent chacun
      // fetchUserData. Le garde de séquence jette les rôles du premier, qui
      // charge pourtant le profil : profil présent, rôles encore vides, et
      // ProtectedRoute envoyait un lien profond vers l'onboarding une fois sur
      // deux (constaté le 29/10/2026 sur /association/:id).
      rolesLoading: !!user && !rolesLoaded,
      subscription,
      subscriptionLoading,
      activeRole,
      setActiveRole,
      refreshSubscription,
      refreshProfile,
      refreshRoles,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
      resetPassword,
      hasRole,
      isAdmin,
      isSuperAdmin,
      isClubStaff,
      isClubAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
